import { FORM_ERROR } from "final-form"
import { navigate } from "gatsby"
import { useEffect } from "react"
import { Field, Form } from "react-final-form"

import {
  amplitudeClient,
  useMutation,
  useQueryClient,
  userClient,
} from "@trueskin-web/apis"
import {
  Button,
  CheckboxControl,
  FieldError,
  Heading,
  ProgressSteps,
} from "@trueskin-web/components"
import { useAuth } from "@trueskin-web/context"
import { getErrorMessage, mustBeTrue } from "@trueskin-web/functions"
import { Routes, Trans } from "@trueskin-web/translations"

import { PhotoSlots, PhotosProvider, usePhotos } from "../../image-upload"

const UploadPhotosButton = (props) => (
  <Button type="submit" isFullWidth {...props}>
    <Trans i18nKey="Start.ModernUploadPhotos.UploadPhotosButton" />
  </Button>
)

const TelemedicineAgreementForm = ({ onSubmit, isDisabled, isLoading }) => (
  <Form
    onSubmit={onSubmit}
    render={({ handleSubmit, valid, submitError }) => (
      <form onSubmit={handleSubmit}>
        <Field
          name="telemedicine"
          component={CheckboxControl}
          type="checkbox"
          validate={mustBeTrue}
          errorMessagePlaceholder
          sx={{
            fontSize: 0,
            textAlign: "left",
          }}
        >
          <Trans i18nKey="Start.ModernUploadPhotos.TelemedicineAgreementForm.consent" />
        </Field>

        {submitError && <FieldError sx={{ my: 4 }}>{submitError}</FieldError>}

        <UploadPhotosButton
          isDisabled={isDisabled || !valid}
          isLoading={isLoading}
        />
      </form>
    )}
  />
)

const ImageUploadContainer = ({ requireTelemedicineAgreement }) => {
  const queryClient = useQueryClient()

  const { hasRequiredPhotos, isUploadImagesLoading, uploadImages } = usePhotos()

  const {
    user: {
      subscription: {
        consultations: [{ id: consultationId }],
      },
    },
  } = useAuth()

  const {
    mutateAsync: submitTelemedicineAgreement,
    loading: isSubmitTelemedicineAgreementLoading,
  } = useMutation(userClient.submitTelemedicineAgreement)

  const onSubmit = async (values) => {
    try {
      if (requireTelemedicineAgreement) {
        await submitTelemedicineAgreement(values)
      }

      await uploadImages({
        consultationId,
        onSuccess: async () => queryClient.refetchQueries("currentUser"),
      })

      amplitudeClient.trackEvent(
        amplitudeClient.ANALYTIC_EVENTS.INITIAL_PICTURES_SUBMITTED
      )

      navigate(Routes.App.Home.url)
    } catch (error) {
      return { [FORM_ERROR]: getErrorMessage(error) }
    }
  }

  useEffect(() => {
    amplitudeClient.trackEvent(
      amplitudeClient.ANALYTIC_EVENTS.INITIAL_PICTURE_UPLOAD_PAGE_VIEWED
    )
  }, [])

  return (
    <div
      sx={{
        display: "grid",
        gridTemplateColumns: [null, "1fr 1fr"],
        gridGap: [5, 6],
        alignItems: "center",
        textAlign: "center",
        mx: "auto",
      }}
    >
      <div>
        <Heading
          sx={{
            fontWeight: "bold",
          }}
        >
          <Trans i18nKey="Start.ModernUploadPhotos.title" />
        </Heading>

        <div
          sx={{
            mt: 3,
            mx: ["5%", "10%"],
          }}
        >
          <Trans i18nKey="Start.ModernUploadPhotos.description" />
        </div>
      </div>

      <div>
        <PhotoSlots
          isModern
          sx={{
            mt: 7,
          }}
        />

        <div
          sx={{
            mt: 4,
            mb: 8,
            fontSize: 1,
            color: "textDark",
          }}
        >
          <Trans i18nKey="Start.ModernUploadPhotos.hint" />
        </div>

        {requireTelemedicineAgreement ? (
          <TelemedicineAgreementForm
            onSubmit={onSubmit}
            isLoading={
              isSubmitTelemedicineAgreementLoading || isUploadImagesLoading
            }
            isDisabled={!hasRequiredPhotos}
          />
        ) : (
          <UploadPhotosButton
            isLoading={isUploadImagesLoading}
            isDisabled={!hasRequiredPhotos}
            onClick={onSubmit}
          />
        )}
      </div>
    </div>
  )
}

const ModernUploadPhotos = ({ requireTelemedicineAgreement }) => (
  <PhotosProvider
    photosOrder={{
      left: 1,
      front: 2,
      right: 3,
    }}
  >
    <ProgressSteps
      sx={{
        pb: 5,
      }}
      steps={4}
      completedSteps={1}
    />
    <ImageUploadContainer
      requireTelemedicineAgreement={requireTelemedicineAgreement}
    />
  </PhotosProvider>
)

export default ModernUploadPhotos
